import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { SeriesChart } from '../components/SeriesChart';
import { fmtDollars, fmtNum } from '../lib/format';

/** Legend chip — matches the inline legend in OverviewScreen. */
function LegendDot({ color, children }: { color: string; children: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} /> {children}
    </span>
  );
}

export function SubscriptionsScreen() {
  const overview = useQuery({
    queryKey: ['subs-overview'],
    queryFn: () => insightsApi.subscriptionsOverview(),
  });
  const series = useQuery({
    queryKey: ['subs-series', 30],
    queryFn: () => insightsApi.subscriptionsSeries(30, 'day'),
  });

  const o = overview.data;
  const points = (series.data?.points ?? []).map((p) => ({
    label: shortDay(p.date),
    value: p.newSubs,
    value2: p.cancellations,
    line: p.activePremium,
  }));

  if (overview.error) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <Card padding={20}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>{(overview.error as Error).message}</span>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard label="Active premium" value={o?.activePremium ?? 0} accent />
        <KpiCard label="Expiring soon" value={o?.premiumExpiringSoon ?? 0} sublabel="next 7 days" />
        <KpiCard
          label="MRR"
          value={o?.revenue.mrr ?? 0}
          format={(n) => fmtDollars(n)}
          sublabel={o ? `avg ${fmtDollars(o.revenue.avgMonthlyPrice)}/mo · ${o.revenue.currency}` : undefined}
        />
        <KpiCard
          label="ARR"
          value={o?.revenue.arr ?? 0}
          format={(n) => fmtDollars(n)}
          sublabel={o ? o.revenue.currency : undefined}
        />
      </div>

      {o?.revenue.isEstimate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-3)', marginTop: -8 }}>
          <Pill tone="warn" size="sm">estimate</Pill>
          Revenue figures (MRR / ARR) are estimated from active subscription counts × average monthly price — not billed amounts.
        </div>
      )}

      {/* Last-30-day funnel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
        <KpiCard label="New · 30d" value={o?.last30d.newSubs ?? 0} />
        <KpiCard label="Renewals · 30d" value={o?.last30d.renewals ?? 0} />
        <KpiCard label="Cancellations · 30d" value={o?.last30d.cancellations ?? 0} />
        <KpiCard label="Expirations · 30d" value={o?.last30d.expirations ?? 0} />
        <KpiCard
          label="Churn rate"
          value={o?.churnRatePct ?? 0}
          format={(n) => `${n.toFixed(1)}%`}
          decimals={1}
          sublabel="trailing 30 days"
        />
      </div>

      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Subscription movement · last 30 days
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', display: 'inline-flex', gap: 12 }}>
            <LegendDot color="var(--brand)">new</LegendDot>
            <LegendDot color="var(--bad)">cancellations</LegendDot>
            <LegendDot color="var(--brand-2, var(--brand))">active premium</LegendDot>
          </span>
        </div>
        {series.isFetching && points.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
        ) : points.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No subscription activity yet.</div>
        ) : (
          <SeriesChart
            data={points}
            height={260}
            valueLabel="new"
            value2Label="cancelled"
            lineLabel="active"
            lineFmt={(n) => fmtNum(n)}
          />
        )}
      </Card>

      <Card padding={18}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
          Active subscriptions by product
        </div>
        {(o?.revenue.byProduct ?? []).map((p) => (
          <div
            key={p.productId}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>{p.productId}</span>
            <Pill tone="brand" size="sm">{fmtNum(p.activeSubs)}</Pill>
          </div>
        ))}
        {o && o.revenue.byProduct.length === 0 && (
          <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No active products.</span>
        )}
      </Card>
    </div>
  );
}

/** "Jul 7" from an ISO date/day string. */
function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
